/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import {
    Component,
    Input,
    Output,
    EventEmitter,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    ViewChild,
    ViewChildren,
    QueryList,
    AfterViewInit,
    OnDestroy,
    ElementRef
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatExpansionPanelHeader } from '@angular/material/expansion';

import { FormsModule } from '@angular/forms';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { A11yModule, FocusMonitor } from '@angular/cdk/a11y';
import { TranslateModule } from '@ngx-translate/core';
import {
    NvAddKeyboardActivateDirective,
    NvMatExpansionPanelHeaderKeyboardFixupDirective
} from 'hig/lib/directives/nv-mat-keyboard-fixups.directive';
import { HeadingFocusableDirective } from 'hig/lib/directives/heading-focusable.directive';
import {
    FilterGroup,
    FilterItem,
    FilterPanelConfig,
    FilterChangeEvent,
    FilterGroupExpandEvent
} from './nv-filter-panel.types';

const DEFAULT_CONFIG: FilterPanelConfig = {
    title: 'Filter',
    clearAllText: 'Clear All',
    showCloseButton: true,
    closeButtonAriaLabel: 'Close filter panel',
    isPanelOpen: true,
    overlayMode: false
};

@Component({
    selector: 'nv-filter-panel',
    templateUrl: './nv-filter-panel.component.html',
    styleUrls: ['./nv-filter-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'nv-filter-panel-host'
    },
    imports: [
        FormsModule,
        FlexModule,
        MatButtonModule,
        MatIconModule,
        MatCheckboxModule,
        MatExpansionModule,
        MatTooltipModule,
        TranslateModule,
        NvAddKeyboardActivateDirective,
        NvMatExpansionPanelHeaderKeyboardFixupDirective,
        HeadingFocusableDirective,
        A11yModule
    ]
})
export class NvFilterPanelComponent implements AfterViewInit, OnDestroy {
    /** Reference to the clear all button for programmatic focus */
    @ViewChild('clearAllButton') clearAllButton: MatButton;

    /** Reference to the first filter group elements for programmatic focus */
    @ViewChildren('firstFilterGroup') firstFilterGroupElements: QueryList<MatCheckbox | MatExpansionPanelHeader>;

    /** List of filter groups to display */
    @Input() filterGroups: FilterGroup[] = [];

    /** Configuration options for the panel */
    @Input()
    set config(value: Partial<FilterPanelConfig>) {
        this._config = { ...DEFAULT_CONFIG, ...value };
    }
    get config(): FilterPanelConfig {
        return this._config;
    }

    /** Whether the clear all button should be disabled */
    @Input() clearAllDisabled = true;

    /** Emitted when the close button is clicked */
    @Output() closePanel = new EventEmitter<void>();

    /** Emitted when the clear all button is clicked */
    @Output() clearAll = new EventEmitter<void>();

    /** Emitted when a filter item's checked state changes */
    @Output() filterChange = new EventEmitter<FilterChangeEvent>();

    /** Emitted when a filter group is expanded */
    @Output() groupExpanded = new EventEmitter<FilterGroupExpandEvent>();

    /** Emitted when a filter group is collapsed */
    @Output() groupCollapsed = new EventEmitter<FilterGroupExpandEvent>();

    private _config: FilterPanelConfig = DEFAULT_CONFIG;
    private monitoredElements: Set<HTMLElement> = new Set();
    private mutationObserver: MutationObserver | null = null;
    private pendingMonitorUpdate = false;

    constructor(
        private elementRef: ElementRef,
        private focusMonitor: FocusMonitor
    ) {}

    ngAfterViewInit(): void {
        this.mutationObserver = new MutationObserver(() => {
            if (!this.pendingMonitorUpdate) {
                this.pendingMonitorUpdate = true;
                requestAnimationFrame(() => {
                    this.setupCheckboxFocusMonitoring();
                    this.pendingMonitorUpdate = false;
                });
            }
        });
        this.mutationObserver.observe(this.elementRef.nativeElement, {
            childList: true,
            subtree: true
        });
        this.setupCheckboxFocusMonitoring();
    }

    /**
     * Sets up FocusMonitor on all checkboxes to enable cdk-keyboard-focused class.
     * This ensures the cdk-keyboard-focused class is added when focused via keyboard.
     */
    private setupCheckboxFocusMonitoring(): void {
        const host = this.elementRef.nativeElement;
        const checkboxes = host.querySelectorAll('.mat-mdc-checkbox');

        checkboxes.forEach((checkbox: HTMLElement) => {
            if (!this.monitoredElements.has(checkbox)) {
                this.monitoredElements.add(checkbox);
                this.focusMonitor.monitor(checkbox, true);
            }
        });
    }

    ngOnDestroy(): void {
        this.mutationObserver?.disconnect();
        this.monitoredElements.forEach(element => {
            this.focusMonitor.stopMonitoring(element);
        });
        this.monitoredElements.clear();
    }

    /**
     * Handles close button click.
     */
    onCloseClick(): void {
        this.closePanel.emit();
    }

    /**
     * Handles clear all button click.
     */
    onClearAllClick(): void {
        this.clearAll.emit();
    }

    /**
     * Handles filter checkbox change.
     */
    onFilterItemChange(group: FilterGroup, item: FilterItem): void {
        this.filterChange.emit({
            group,
            item,
            checked: item.checked
        });
    }

    /**
     * Handles expansion panel opened.
     */
    onPanelOpened(group: FilterGroup): void {
        group.expanded = true;
        this.groupExpanded.emit({ group, expanded: true });
    }

    /**
     * Handles expansion panel closed.
     */
    onPanelClosed(group: FilterGroup): void {
        group.expanded = false;
        this.groupCollapsed.emit({ group, expanded: false });
    }

    /**
     * Track by function for filter groups.
     */
    trackByGroup(index: number, group: FilterGroup): string {
        return group.id;
    }

    /**
     * Track by function for filter items.
     */
    trackByItem(index: number, item: FilterItem): string {
        return item.id;
    }

    /**
     * Gets the tab index for interactive elements based on panel open state.
     */
    getTabIndex(): number {
        return this.config.isPanelOpen ? 0 : -1;
    }

    /**
     * Focuses the first filter element. Used for accessibility after panel opens.
     */
    focusFirstFilterElement(): void {
        if (this.firstFilterGroupElements?.first) {
            const element = this.firstFilterGroupElements.first;
            if (element instanceof MatCheckbox) {
                element.focus();
            } else if (element instanceof MatExpansionPanelHeader) {
                element.focus();
            }
        }
    }

    /**
     * Focuses the clear all button. Used for accessibility.
     */
    focusClearAllButton(): void {
        this.clearAllButton?.focus();
    }
}
